import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { Alert } from 'reactstrap';
import type { MyProductReview } from '../../../../../music24-api/src/productReviews/productReviewTypes';
import { TypedMemo } from '../../common/types';
import { reviewMessages } from './reviewMessages';
import { statusViewOf } from './reviewRules';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the review screen; each part uses its own slice of it */
import styles from './Review.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReviewStatusLineProps {
  review: MyProductReview;
}

/**
 * The status of a saved review (design review-form a7, a8, a9): in review, published or not
 * published. A published review also gets the note that a saved edit goes back to the team.
 */
const ReviewStatusLineComponent: React.FC<ReviewStatusLineProps> = ({ review }) => {
  const intl = useIntl();
  const view = statusViewOf(review.status);
  const date = (iso: string) => intl.formatDate(iso, { day: 'numeric', month: 'short', year: 'numeric' });

  const chip = () => {
    if (view === 'published') {
      return { label: reviewMessages.statusPublished, className: styles.chipPublished };
    }
    if (view === 'notPublished') {
      return { label: reviewMessages.statusNotPublished, className: styles.chipNotPublished };
    }
    return { label: reviewMessages.statusInReview, className: styles.chipInReview };
  };
  const line = () => {
    if (view === 'published') {
      return intl.formatMessage(reviewMessages.statusPublishedLine, {
        date: date(review.approvedAt ?? review.updatedAt),
      });
    }
    if (view === 'inReview') {
      return intl.formatMessage(reviewMessages.statusInReviewLine, { date: date(review.updatedAt) });
    }
    return null;
  };
  const { label, className } = chip();
  const text = line();

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2" data-testid="review-status">
        <span className={clsx(styles.chip, className)}>{intl.formatMessage(label)}</span>
        {text !== null && <span className={styles.hint}>{text}</span>}
      </div>
      {view === 'published' && (
        <Alert color="secondary" className="mb-0">
          {intl.formatMessage(reviewMessages.publishedEditNote)}
        </Alert>
      )}
      {view === 'notPublished' && (
        <Alert color="secondary" className="mb-0">
          {intl.formatMessage(reviewMessages.notPublishedNote)}
        </Alert>
      )}
    </>
  );
};

export const ReviewStatusLine = TypedMemo(ReviewStatusLineComponent);
